export type ProductVariant =
  | 'non-foil'
  | 'foil'
  | 'specialty-foil'
  | 'extended'
  | 'showcase';

// Valores canónicos de la columna `products.quality` del backend. Los escribe
// el admin como snake_case en inglés (ver el formulario de producto). No hay
// constraint de DB, pero este es el único contrato aceptado en el lado público;
// cualquier otro valor se trata como desconocido.
export type ProductCondition =
  | 'mint'
  | 'lightly_played'
  | 'moderate_played'
  | 'heavily_played'
  | 'damaged';

export type ProductType = 'single' | 'accessory' | 'collectible' | 'sealed' | 'board-game';

export interface ProductImage {
  id: number;
  name: string;
  is_back?: boolean;
}

export interface StockAlertMeta {
  has_alert: boolean;
  subscriber_count: number;
  product_alert_id: number | null;
}

export interface ProductCategoryRef {
  id: number;
  name: string;
  /**
   * Slug autoritativo de la categoría (espejo de `Category.slug`). Lo emite
   * el backend en cada serialización de `Product.categories[]` y en `parent`.
   * Siempre presente — el observer garantiza que toda categoría tiene slug.
   */
  slug: string;
  parent?: ProductCategoryRef | null;
  pivot?: { product_id: number; category_id: number };
}

export interface Product {
  // Identidad
  id: string | number;
  name: string;

  // UI fields (usados por mocks y cards actuales)
  game: string;
  setName: string;
  setCode: string;
  price?: number;
  originalPrice?: number | null;
  currency: string;
  imageUrl: string;
  imageUrl2?: string;
  variant: ProductVariant;
  condition?: ProductCondition;
  productType?: ProductType;
  stock: number;
  isNew: boolean;
  isOnSale: boolean;
  isPreSale: boolean;
  language?: string;
  collectorNumber?: string;

  // Quickview
  rarity?: string;
  cardType?: string;
  hp?: string;
  stage?: string;
  shortDescription?: string;

  // Mecánicas de juego (OP/MTG) — string|null para soportar notaciones tipo
  // "X", "*", "1+*". Productos Pokémon y legacy llegan con todo null. snake_case
  // intencional: coincide con la API y no necesita bridge en toUiProduct.
  color?: string | null;
  cost?: string | null;
  mana_cost?: string | null;
  power?: string | null;
  toughness?: string | null;
  subtype?: string | null;
  counter?: string | null;
  card_attribute?: string | null;

  // Ficha
  cardText?: string;
  slug?: string;
  artist?: string;
  description?: string;
  // Items inline de related_products / frequently_bought_together en
  // /show-web-product devuelven sólo el filename de la primera imagen como
  // `image` (singular), sin el array `images` ni un `imageUrl` resuelto.
  image?: string;
  images?: string[] | ProductImage[];
  hasDoubleFace?: boolean;
  backImageUrl?: string;
  specs?: Record<string, string>;
  brand?: string;
  category?: string;
  releaseDate?: string;
  contents?: string;

  // API fields (perronstore-api)
  sale_price?: number;
  taxes?: number;
  reference?: string;
  internal_reference?: string;
  is_foil?: boolean;
  is_holo?: boolean;
  quality?: ProductCondition | string;
  product_type?: string;
  prepurchase?: boolean;
  /**
   * Interruptor maestro de la restricción por Perrón Points (R7): ya NO depende
   * de `prepurchase`. Un producto normal en stock puede estar restringido.
   */
  early_access?: boolean;
  /**
   * Umbral CRUDO de la columna, con la semántica de tres estados de R5:
   * `null` → hereda el global · `0` → solo requiere sesión · `N ≥ 1` → saldo.
   * La tienda no lo interpreta (eso es del backoffice); consume
   * `early_access_threshold`. Se conserva porque llega en el payload y
   * distinguir `null` de `0` aquí evita normalizaciones destructivas.
   */
  early_access_min_points?: number | null;
  /**
   * Umbral RESUELTO por el servidor: `null` si el producto no está restringido,
   * y el entero efectivo (global heredado o propio) si lo está. Es la fuente de
   * verdad del badge y del corte en «Agregar al carrito».
   */
  early_access_threshold?: number | null;
  prepurchase_stock?: number;
  prepurchase_due_date?: string | null;
  prepurchase_limit?: number;
  max_units_per_order?: number;
  max_units_per_client?: number;
  enabled?: boolean;
  discount?: boolean;
  discountQty?: number;
  min_stock?: number;
  card_id?: number | null;
  categories?: ProductCategoryRef[];
  stock_alert?: StockAlertMeta;
  related_products?: Product[];
  frequently_bought_together?: Product[];
  created_at?: string;
  updated_at?: string;
}

export const LOW_STOCK_THRESHOLD = 5;

export const PREPURCHASE_SOLD_OUT_MESSAGE =
  '¡Mantente atento(a) el día del lanzamiento! Tendremos disponibles algunas unidades extra 🥳';

/**
 * Stock efectivo de precompra. La API puede serializar `prepurchase_stock`
 * como string (DECIMAL) o int; `Number()` normaliza ambos antes de comparar.
 */
export function getPrepurchaseStock(
  product: Product | null | undefined,
): number {
  const stock = Number(product?.prepurchase_stock ?? 0);
  return Number.isFinite(stock) ? stock : 0;
}

/**
 * `true` si el producto es de precompra y ya no quedan cupos.
 */
export function isPrepurchaseSoldOut(
  product: Product | null | undefined,
): boolean {
  if (!product || (product.prepurchase !== true && product.isPreSale !== true)) {
    return false;
  }
  return getPrepurchaseStock(product) <= 0;
}

/**
 * Único criterio de producto restringido por Perrón Points. Desde R7,
 * `early_access` es el interruptor maestro y NO exige precompra: un producto
 * normal en stock puede estar restringido, que es justo el caso que motivó la
 * extensión del spec.
 *
 * Mismo predicado para el badge «Acceso VIP» (card, listado, zoom, ficha y
 * modal de precompra) y para el corte de «Agregar al carrito».
 */
export function isEarlyAccessProduct(product: Product | null | undefined): boolean {
  return !!product && product.early_access === true;
}

export const CONDITION_LABELS: Record<ProductCondition, string> = {
  'mint': 'Mint/Near Mint',
  'lightly_played': 'Lightly Played',
  'moderate_played': 'Moderate Played',
  'heavily_played': 'Heavily Played',
  'damaged': 'Damaged',
};

// Tono semántico por condición — mapea a modifiers de `app-badge` / clases
// existentes. Mantener alineado con el admin (verde→azul→amarillo→naranja→rojo)
// sin introducir Tailwind colors crudos (ver CLAUDE.md: variables semánticas).
export const CONDITION_TONE: Record<ProductCondition, 'success' | 'info' | 'warning' | 'alert' | 'danger'> = {
  'mint': 'success',
  'lightly_played': 'info',
  'moderate_played': 'warning',
  'heavily_played': 'alert',
  'damaged': 'danger',
};

export const VARIANT_LABELS: Record<ProductVariant, string> = {
  'non-foil': 'Non-Foil',
  'foil': 'Foil',
  'specialty-foil': 'Specialty Foil',
  'extended': 'Extended Art',
  'showcase': 'Showcase',
};

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  'single': 'Single',
  'accessory': 'Accesorio',
  'collectible': 'Coleccionable',
  'sealed': 'Sellado',
  'board-game': 'Juego de Mesa',
};

/**
 * Códigos de idioma (ISO 639-1) → etiqueta en castellano. `language` en
 * `Product` es `string` libre (no enum) porque la API puede devolver
 * códigos adicionales; de ahí que el Record sea `Record<string, string>`.
 */
export const LANGUAGE_LABELS: Record<string, string> = {
  'en': 'Inglés',
  'es': 'Español',
  'ja': 'Japonés',
};

export interface AddToCartEvent {
  product: Product;
  quantity: number;
}

export interface Paginated<T> {
  data: T[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number | null;
  to: number | null;
  first_page_url?: string;
  last_page_url?: string;
  next_page_url?: string | null;
  prev_page_url?: string | null;
  path?: string;
  links?: Array<{ url: string | null; label: string; active: boolean }>;
}

export interface AvailableProductsParams {
  search?: string;
  skipZeroStock?: boolean;
  per_page?: number;
  page?: number;
  // Sort unificado (compartido con /category-web-products). No combinar con sort_by/order.
  sort?: 'popular' | 'newest' | 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc';
  sort_by?: 'name' | 'sale_price' | 'updated_at' | 'created_at';
  order?: 'asc' | 'desc';
  skip_paging?: boolean;
  /**
   * Incluye productos con `prepurchase=true` en el listado general. Default
   * backend = false (a partir del cambio que añade `availableForWeb` a este
   * endpoint). El catálogo nuevo lo envía siempre en `1`.
   */
  include_prepurchase?: boolean;
  /**
   * Filtra sólo productos con `discount=true`. Usado por el bloque de ofertas
   * de la home. Backend: `scopeOnDiscount` en Product.
   */
  on_discount?: boolean;
}

export interface CompareResponse {
  data: Product[];
  missing_ids: number[];
  unavailable_ids: number[];
}

export type ProductNotFoundReason = 'not_found' | 'disabled';

export class ProductNotFoundError extends Error {
  constructor(
    public readonly slug: string,
    public readonly reason: ProductNotFoundReason,
    message?: string,
  ) {
    super(message ?? (reason === 'disabled'
      ? 'El producto no está disponible'
      : `Producto "${slug}" no encontrado`));
    this.name = 'ProductNotFoundError';
  }
}
